<template>
  <widget-template-selector
    ref="creator"
    :title="is_re_apply_mode ? 'Change Template' : 'Create Guest Check-In Form'"
    :subtitle="is_re_apply_mode ? 'Choose a new template to update your form styling and layout. Your custom settings will be preserved.' : 'Choose a template and customize the basic styles. You\'ll be able to customize the imagery, styles, and content further on the next page.'"
    :organization_id="organization_id"
    :default_background_image="'https://files.redchirp.com/xcduzazsqfsxurbdbuwqxwwyvdyybqex.jpg'"
    :default_side_image="'https://files.redchirp.com/xqyarbyxardduytvbwevbzcvxtrdzfrr.jpg'"
    :initial_form_data="initial_form_data"
    :storage_key_prefix="'preferred_guest_check_in_template'"
    :create_endpoint="create_endpoint"
    :validate_custom_settings="validate_custom_settings_fn"
    :is_re_apply_mode="is_re_apply_mode"
    @form-created="onFormCreated"
    @form-valid-changed="$emit('form-valid-changed', $event)"
    @validation-warnings-changed="$emit('validation-warnings-changed', $event)"
  >
    <template #settings="{ form_data }">
      <template v-if="!is_re_apply_mode">
        <v-col cols="12">
          <div class="settings-section" data-validation-target="formNameInput">
            <div class="settings-section-header">
              <div>
                <v-icon color="primary" size="20" class="mr-2">mdi-cog</v-icon>
                <span class="settings-section-title">Form Settings</span>
              </div>
              <v-chip v-if="!form_data.internal_label" color="error" small outlined class="ml-1">
                <v-icon small left>mdi-alert-circle</v-icon>
                Required
              </v-chip>
            </div>
  
            <v-text-field
              v-model="form_data.internal_label"
              label="Internal Form Name"
              placeholder="Enter a name for this form"
              outlined
              dense
              persistent-hint
              hint="This is only used internally and is not visible to guests"
              :rules="[rules.required]"
              hide-details="auto"
            />
          </div>
        </v-col>
  
        <v-col cols="12">
          <div class="settings-section" data-validation-target="consentSettingsSection">
            <div class="settings-section-header">
              <div>
                <v-icon color="primary" size="20" class="mr-2">mdi-shield-check</v-icon>
                <span class="settings-section-title">Consent Settings</span>
              </div>
              <v-chip
                v-if="!isConsentSettingsValid(form_data)"
                color="error"
                small
                outlined
                class="ml-1"
              >
                <v-icon small left>mdi-alert-circle</v-icon>
                Required
              </v-chip>
            </div>
  
            <v-select
              label="Email Consent Gathering"
              v-model="form_data.email_consent_gathering_state"
              :items="filtered_consent_gathering_options"
              outlined
              dense
              hide-details="auto"
              hint="Choose how initial consent should be gathered from guests who provide an email address on the form."
              persistent-hint
              class="mb-4"
            />
            
            <v-select
              label="Phone Consent Gathering"
              v-model="form_data.phone_number_consent_gathering_state"
              :items="filtered_consent_gathering_options"
              outlined
              dense
              hide-details="auto"
              hint="Choose how initial consent should be gathered from guests who provide a phone number on the form."
              persistent-hint
            />
          </div>
        </v-col>
      </template>

      <!-- In re-apply mode, show info about what will be preserved -->
      <v-col
        v-else
        cols="12"
      >
        <v-alert
          type="info"
          outlined
        >
          <div class="font-weight-bold mb-2">Your custom settings will be preserved</div>
          <div class="text-body-2">
            Only the template styling and layout will be updated. Your settings name, consent configuration, CRM
            settings, and
            list assignments will remain unchanged.
          </div>
        </v-alert>
      </v-col>
    </template>

    <template
      #template-gallery="{ selected_template, theme, organization_name, background_image_url, side_image_url, logo_url, use_logo, form_data, on_template_selected, on_template_deselected }"
    >
      <m-guest-check-in-template-gallery
        :selected_template_id="selected_template?.id"
        :theme="theme"
        :organization_name="organization_name"
        :background_image_url="background_image_url"
        :side_image_url="side_image_url"
        :logo_url="logo_url"
        :use_logo="use_logo"
        :user_consent_settings="form_data"
        @template-selected="on_template_selected"
        @template-deselected="on_template_deselected"
      />
    </template>
  </widget-template-selector>
</template>

<script>
import WidgetTemplateSelector from '../widget/widget-template-selector.vue';
import mGuestCheckInTemplateGallery from './m-guest-check-in-template-gallery.vue';

export default {
  name: 'GuestCheckInTemplateSelector',

  components: {
    WidgetTemplateSelector,
    mGuestCheckInTemplateGallery,
  },

  props: {
    organization_id: { type: String, required: true },
    is_re_apply_mode: { type: Boolean, default: false },
    form_id: { type: String, default: null },
  },

  data() {
    return {
      initial_form_data: {
        internal_label: '',
        email_consent_gathering_state: '',
        phone_number_consent_gathering_state: '',
      },
      rules: { required: value => !!value || 'This field is required' },
    };
  },

  computed: {
    filtered_consent_gathering_options() {
      const allOptions = this.$rc.enumChoices('form_consent_gathering_state', false);
      return allOptions.filter(option => option.value !== 'none');
    },

    create_endpoint() {
      if (this.is_re_apply_mode && this.form_id) {
        return `PUT: /api/v1/guest-check-in/forms/${this.form_id}/re-apply-template`;
      }
      return 'PUT: /api/v1/guest-check-in/forms/create-stub';
    },

    validate_custom_settings_fn() {
      // In re-apply mode, we don't need to validate custom settings (consent, internal_label)
      // because they're already set on the existing form
      if (this.is_re_apply_mode) {
        return () => true;
      }
      return this.isConsentSettingsValid;
    },
  },

  methods: {
    isConsentSettingsValid(form_data) {
      return (
        !!form_data.email_consent_gathering_state &&
        !!form_data.phone_number_consent_gathering_state &&
        form_data.email_consent_gathering_state !== 'any_enum_value' &&
        form_data.phone_number_consent_gathering_state !== 'any_enum_value'
      );
    },

    onFormCreated(data) {
      this.$emit('form-created', data.guest_check_in_form_id);
    },

    createForm() {
      if (this.$refs.creator) {
        this.$refs.creator.createForm();
      }
    },

    fixWarning(warning) {
      if (this.$refs.creator) {
        this.$refs.creator.fixWarning(warning);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.settings-section {
  background: var(--color-bg-shaded-light);
  border-radius: var(--border-radius-default);
  padding: 1.25rem;
  height: 100%;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #c8ccd0;
  }
}

.settings-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 0.5rem;
}

.settings-section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}
</style>
